import { DotLottie, DotLottieReact } from '@lottiefiles/dotlottie-react'
import { useAudio } from '@providers'
import classNames from 'classnames'
import { motion } from 'framer-motion'
import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { QuestShortDetails, useOverlayActions } from '@store'

import { CircularText } from '../CircularText'
import QuestAnimation from '../assets/Quest.json'
import { QuestLayoutConfig, QuestViewState } from '../types/QuestCircleTypes'
import { DoneIcon } from './DoneIcon'
import styles from './QuestCircle.module.scss'
import { StarIcon } from '../icons/StarIcon'

const matrixEffectTimeout = 4000
const DONE_ICON_SHOW_DELAY = 2000

interface Props {
  initialState: QuestViewState
  pageIndex: number
  layoutConfig: QuestLayoutConfig
  quest?: QuestShortDetails
  visible?: boolean
  isAnimated?: boolean
  questCircleOpacity?: number
  classNamePicture?: CSSProperties
  onClick: () => void
}

export const QuestCircle = ({
  initialState,
  visible,
  quest,
  isAnimated,
  pageIndex,
  layoutConfig,
  classNamePicture,
  onClick,
  questCircleOpacity = 0.3,
}: Props) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [dotLottie, setDotLottie] = useState<DotLottie | null>(null)
  const [revealQuestPicture, setRevealQuestPicture] = useState(false)
  const [runQuestAnimation, setRunQuestAnimation] = useState(false)
  const [revealQuestTitle, setRevealQuestTitle] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [showDoneIcon, setShowDoneIcon] = useState(false)
  const questRef = useRef<HTMLDivElement>(null)
  const [radius, setRadius] = useState('0rem')
  const rotationCompensation = (-90 * (pageIndex + 1)) % 360
  const { playSwitchOff } = useAudio()
    const { setOverlayQueue } = useOverlayActions()

  useEffect(() => {
    if (dotLottie && runQuestAnimation) {
      dotLottie.play()
    }
  }, [dotLottie, runQuestAnimation])

  useEffect(() => {
    const updateRadius = () => {
      if (questRef.current) {
        const { width } = questRef.current.getBoundingClientRect()
        const calculatedRadius = `-${width * 0.75}rem`
        setRadius(calculatedRadius)
      }
    }

    updateRadius()
    window.addEventListener('resize', updateRadius)
    return () => window.removeEventListener('resize', updateRadius)
  }, [])

  useEffect(() => {
    let doneIconTimeout: NodeJS.Timeout
    let revealQuestTitleTimeout: NodeJS.Timeout
    let runQuestAnimationTimeout: NodeJS.Timeout
    let revealQuestPictureTimeout: NodeJS.Timeout
    revealQuestTitleTimeout = setTimeout(() => {
      setRevealQuestTitle(true)
    }, 1500)

    if (initialState === QuestViewState.ShouldOpen) {
      runQuestAnimationTimeout = setTimeout(() => {
        setRunQuestAnimation(true)
      }, 2500)
      revealQuestPictureTimeout = setTimeout(() => {
        setRevealQuestPicture(true)
      }, matrixEffectTimeout)
    }

    if (initialState === QuestViewState.Showed) {
      setIsOpen(true)
      doneIconTimeout = setTimeout(() => {
        setShowDoneIcon(true)
      }, DONE_ICON_SHOW_DELAY)
    }

    return () => {
      clearTimeout(doneIconTimeout)
      clearTimeout(revealQuestTitleTimeout)
      clearTimeout(runQuestAnimationTimeout)
      clearTimeout(revealQuestPictureTimeout)
    }
  }, [initialState])

  const navigateToQuest = useCallback(() => {
    if (quest && !quest.isAvailable && !quest.isSolved && initialState === QuestViewState.ShouldOpen) {
      setOverlayQueue([{
        type: 'openNewQuest',
        payload: {
          questSlug: quest?.slug
        },
      }])
      return;
    }
    if (quest?.slug && initialState !== QuestViewState.Closed) {
      playSwitchOff()
      onClick()
      navigate(`/quest/${quest.slug}`)
    }
  }, [quest?.slug, initialState])

  const isClickable = quest?.slug && initialState !== QuestViewState.Closed
  const isQuestCompleted = Boolean(quest && quest.isSolved && quest.starsTotal === quest.starsCollected)
  const textOffset = layoutConfig.textOffset

  // Calculate DoneIcon position at the edge of the circle (top-right, 45 degrees)
  const getDoneIconStyle = () => {
    const imageWidth = parseFloat(layoutConfig.imageSize.width) || 0
    const imageHeight = parseFloat(layoutConfig.imageSize.height) || 0
    const radius = Math.max(imageWidth, imageHeight) / 2
    const iconSize = 16 // DoneIcon is 16rem
    // Position at 45 degrees (top-right): sin(45°) = cos(45°) ≈ 0.707
    // Start from center of image, move to edge
    const centerX = imageWidth / 2
    const centerY = imageHeight / 2
    const offsetX = radius * 0.707 // Distance from center to edge at 45°
    const offsetY = -radius * 0.707
    // Final position: center + offset - half icon size to center the icon
    const finalX = centerX + offsetX - iconSize / 2
    const finalY = centerY + offsetY - iconSize / 2
    return {
      left: `${finalX}rem`,
      top: `${finalY}rem`,
    }
  }

  return (
    <motion.div
      key={`quest-${quest?.id || 'empty'}-${pageIndex}`}
      ref={questRef}
      className={classNames([
        styles.quest,
        { [styles.quest_animated]: isAnimated },
        { [styles.quest_clickable]: isClickable },
      ])}
      onClick={navigateToQuest}
      style={{
        ...layoutConfig.position,
        fontSize: layoutConfig.fontSize,
        cursor: isClickable ? 'pointer' : 'not-allowed',
      }}
      animate={{
        rotate: rotationCompensation,
        opacity: quest ? 1 : 0.3,
      }}
      transition={{
        duration: 0.3,
        ease: 'easeInOut',
      }}
      initial={{ opacity: 0 }}
    >
      <div
        className={styles.rippleContainer}
        style={{ ...layoutConfig.imageSize }}
      >
        {initialState === QuestViewState.ShouldOpen && (
          <>
            <div className={styles.ripple} />
            <div className={styles.ripple} />
            <div className={styles.ripple} />
            <div className={styles.ripple} />
          </>
        )}
        <motion.div
          className={classNames([
            styles.image,
            revealQuestPicture && styles.image_opening,
            isOpen && styles.image_open,
          ])}
          style={{
            ...layoutConfig.imageSize,
          }}
          animate={{
            opacity: quest?.image ? 1 : questCircleOpacity,
          }}
          transition={{
            duration: 0.6,
            ease: 'easeInOut',
          }}
        >
          <img
            src={quest?.image}
            className={styles.picture}
            style={classNamePicture}
          />
        </motion.div>
      </div>
      <div
        className={classNames([styles.title, visible ? styles.visible : ''])}
      >
        <div
          className={styles.wrapperQuest}
          style={{
            ...layoutConfig.size,
          }}
        >
          <CircularText
            key={`circular-${quest?.id || 'empty'}-${pageIndex}`}
            offset={textOffset}
            reveal={
              initialState === QuestViewState.ShouldOpen
                ? revealQuestTitle
                : false
            }
            matrixEffectTimeout={matrixEffectTimeout}
            text={t(`main.quests.${quest?.slug}`) || ''}
            radius={radius}
            state={initialState}
          />
        </div>
      </div>

      <div className={styles.animation}>
        <DotLottieReact
          data={QuestAnimation}
          dotLottieRefCallback={setDotLottie}
        />
      </div>
      {isQuestCompleted && showDoneIcon && (
        <motion.div
          className={styles.doneIcon}
          style={getDoneIconStyle()}
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            duration: 0.5,
            ease: 'easeOut',
          }}
        >
          <DoneIcon />
        </motion.div>
      )}
      {quest?.isSolved && <div
        className={classNames(styles.starsInfo, { [styles['starsInfo__circle']]: isQuestCompleted })}
      >
        {!isQuestCompleted ? <span className={styles.starsInfoText}>{quest.starsCollected}/{quest.starsTotal}</span> : null}
        <StarIcon />
      </div>}
    </motion.div>
  )
}
